import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getSubjects, getChapters, createTest, getRetiredCount } from '../../api';

const TEST_TYPES = [
  { id: 'sprint', label: 'Sprint', questions: 30, minutes: 25, icon: 'S', desc: '30 Questions / 25 Minutes' },
  { id: 'semi', label: 'Semi', questions: 45, minutes: 35, icon: 'M', desc: '45 Questions / 35 Minutes' },
];

export default function TestBuilder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [error, setError] = useState('');

  const subjects = useQuery({ queryKey: ['subjects'], queryFn: () => getSubjects().then(r => r.data) });
  const chapters = useQuery({
    queryKey: ['chapters', selectedSubject?.id],
    queryFn: () => getChapters(selectedSubject.id).then(r => r.data),
    enabled: !!selectedSubject,
  });
  const retired = useQuery({ queryKey: ['retiredCount'], queryFn: () => getRetiredCount().then(r => r.data) });

  const createMut = useMutation({
    mutationFn: createTest,
    onSuccess: (res) => navigate(`/student/test/${res.data.id}`),
    onError: (e) => setError(e.response?.data?.detail || 'Failed to create test. Make sure questions exist for selected chapters.'),
  });

  const toggleChapter = (chapter) => {
    setSelectedChapters(prev =>
      prev.find(c => c.id === chapter.id) ? prev.filter(c => c.id !== chapter.id) : [...prev, chapter]
    );
  };

  const startRetiredTest = () => {
    setError('');
    const n = retired.data?.count || 0;
    if (!n) { setError('You have no retired questions yet. Retire questions while reviewing a test.'); return; }
    createMut.mutate({
      title: 'Retired Questions Test',
      time_limit_minutes: 180,
      slots: [{ subject_id: 0, chapter_id: null, topic_id: null, count: n, mode: 'retired' }],
    });
  };

  const handleStart = () => {
    setError('');
    const type = TEST_TYPES.find(t => t.id === selectedType);
    createMut.mutate({
      title: `${selectedSubject.name} - ${type.label}`,
      time_limit_minutes: type.minutes,
      slots: [{ subject_id: selectedSubject.id, chapter_id: null, topic_id: null, count: type.questions }],
      chapter_ids: selectedChapters.map(c => c.id),
    });
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      <h1 style={{ fontSize: 26, marginBottom: 4 }}>Take a Test</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 32, fontSize: 14 }}>Choose your subject, chapters and test type</p>

      {(retired.data?.count || 0) > 0 && (
        <button onClick={startRetiredTest} disabled={createMut.isPending}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 20px', marginBottom: 24, borderRadius: 14, border: '2px solid #6EE7B7', background: '#ECFDF5', cursor: 'pointer', textAlign: 'left' }}>
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 700, fontSize: 16, fontFamily: 'Sora', color: '#047857' }}>Retired Questions Test</span>
            <span style={{ fontSize: 13, color: '#059669' }}>Practice only questions you've retired. Answer one wrong and it returns to your active bank.</span>
          </span>
          <span style={{ flexShrink: 0, background: '#10B981', color: 'white', borderRadius: 20, padding: '6px 14px', fontWeight: 700, fontSize: 13 }}>{retired.data.count} Qs</span>
        </button>
      )}

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32, gap: 0 }}>
        {['Subject', 'Chapters', 'Test Type'].map((label, i) => {
          const num = i + 1;
          const active = step === num;
          const done = step > num;
          return (
            <div key={label} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, background: done ? 'var(--success)' : active ? 'var(--primary)' : 'var(--border)', color: (done || active) ? 'white' : 'var(--text-muted)', marginBottom: 6 }}>
                  {done ? 'ok' : num}
                </div>
                <span style={{ fontSize: 12, color: active ? 'var(--primary)' : 'var(--text-muted)', fontWeight: active ? 600 : 400 }}>{label}</span>
              </div>
              {i < 2 && <div style={{ height: 2, flex: 0.3, background: done ? 'var(--success)' : 'var(--border)', marginBottom: 22 }} />}
            </div>
          );
        })}
      </div>

      {step === 1 && (
        <div>
          <h2 style={{ fontSize: 18, marginBottom: 16 }}>Choose Subject</h2>
          {subjects.isLoading ? <div className="spinner" /> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
              {subjects.data?.map(s => (
                <button key={s.id} onClick={() => { setSelectedSubject(s); setSelectedChapters([]); setStep(2); }}
                  style={{ padding: '28px 20px', borderRadius: 14, border: '2px solid var(--border)', background: 'white', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--primary-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'white'; }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>
                    {s.name === 'Physics' ? 'Ph' : s.name === 'Chemistry' ? 'Ch' : 'Bio'}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 18, fontFamily: 'Sora' }}>{s.name}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(1)}>Back</button>
            <h2 style={{ fontSize: 18 }}>Choose Chapters — {selectedSubject?.name}</h2>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selectedChapters.length} selected</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelectedChapters(chapters.data || [])}>Select All</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelectedChapters([])}>Clear</button>
            </div>
          </div>
          {chapters.isLoading ? <div className="spinner" /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
              {chapters.data?.map(c => {
                const selected = !!selectedChapters.find(ch => ch.id === c.id);
                return (
                  <button key={c.id} onClick={() => toggleChapter(c)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 10, border: `2px solid ${selected ? 'var(--primary)' : 'var(--border)'}`, background: selected ? 'var(--primary-light)' : 'white', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                    <div style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, border: `2px solid ${selected ? 'var(--primary)' : 'var(--border)'}`, background: selected ? 'var(--primary)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {selected && <span style={{ color: 'white', fontSize: 13, fontWeight: 700 }}>v</span>}
                    </div>
                    <span style={{ fontSize: 15, fontWeight: selected ? 600 : 400, color: selected ? 'var(--primary-dark)' : 'var(--text)', flex: 1 }}>{c.name}</span>
                    <span style={{ fontSize: 12, color: selected ? 'var(--primary)' : 'var(--text-muted)', background: selected ? 'var(--primary-light)' : 'var(--bg)', border: `1px solid ${selected ? 'var(--primary)' : 'var(--border)'}`, borderRadius: 20, padding: '2px 10px', flexShrink: 0, fontWeight: 500 }}>{c.question_count ?? 0} Qs</span>
                  </button>
                );
              })}
            </div>
          )}
          <button className="btn btn-primary" disabled={selectedChapters.length === 0} onClick={() => setStep(3)} style={{ padding: '12px 32px', fontSize: 15 }}>
            Continue with {selectedChapters.length} chapter{selectedChapters.length !== 1 ? 's' : ''}
          </button>
        </div>
      )}

      {step === 3 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setStep(2)}>Back</button>
            <h2 style={{ fontSize: 18 }}>Choose Test Type</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            {TEST_TYPES.map(type => (
              <button key={type.id} onClick={() => setSelectedType(type.id)}
                style={{ padding: '28px 24px', borderRadius: 14, textAlign: 'center', cursor: 'pointer', border: `2px solid ${selectedType === type.id ? 'var(--primary)' : 'var(--border)'}`, background: selectedType === type.id ? 'var(--primary-light)' : 'white', transition: 'all 0.15s' }}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: selectedType === type.id ? 'var(--primary)' : 'var(--bg)', color: selectedType === type.id ? 'white' : 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 22, margin: '0 auto 12px' }}>{type.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 20, fontFamily: 'Sora', marginBottom: 6, color: selectedType === type.id ? 'var(--primary-dark)' : 'var(--text)' }}>{type.label}</div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{type.desc}</div>
              </button>
            ))}
          </div>
          {selectedType && (
            <div style={{ background: 'var(--primary-light)', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
              <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--primary-dark)' }}>Test Summary</div>
              <div style={{ fontSize: 14, color: 'var(--primary-dark)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span>Subject: <strong>{selectedSubject?.name}</strong></span>
                <span>Chapters: <strong>{selectedChapters.length} selected</strong></span>
                <span>Questions: <strong>{TEST_TYPES.find(t => t.id === selectedType)?.questions}</strong></span>
                <span>Time: <strong>{TEST_TYPES.find(t => t.id === selectedType)?.minutes} minutes</strong></span>
              </div>
            </div>
          )}
          {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
          <button className="btn btn-primary" disabled={!selectedType || createMut.isPending} onClick={handleStart}
            style={{ padding: '14px 40px', fontSize: 16, width: '100%', justifyContent: 'center' }}>
            {createMut.isPending ? 'Setting up...' : 'Start Test'}
          </button>
        </div>
      )}
    </div>
  );
}
